import { Fragment } from 'react';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import { company, telHref } from '../../site/company';
import { CookiePreferences } from './cookie-preferences';
import { ClockIcon, MailIcon, ShieldIcon, WarningIcon } from './policy-icons';
import type { PolicySection } from './policy-sections';
import type { PolicyTab } from './policy-tab';

interface PolicyDocumentProps {
    sections: readonly PolicySection[];
    /** Prefixes every anchor, because both documents are in the DOM and both have an `intro`. */
    tab: PolicyTab;
}

/**
 * One of the two legal documents, rendered from its section list.
 *
 * The anchors carry the tab's name because **both documents are always in the DOM** — the hidden
 * one is hidden, not unmounted, so the browser's own find-in-page still reaches it and so does
 * anything reading the rendered markup. Unprefixed, `#intro` would appear twice and the contents
 * link would land on whichever the browser found first.
 */
export function PolicyDocument({ sections, tab }: PolicyDocumentProps) {
    const { t } = useTranslation();

    return (
        <>
            <p className="last-updated">
                <ClockIcon />
                <span>{t('policy.misc.updated')}</span>
            </p>

            {sections.map((section, index) => (
                <Fragment key={section.id}>
                    {index > 0 && <hr className="section-rule" />}

                    <section className="article-section" id={`${tab}-${section.id}`}>
                        <span className="article-section-label">{t(section.label)}</span>
                        <h2>{t(section.heading)}</h2>
                        <p>{t(section.intro)}</p>

                        {section.groups?.map((group) => (
                            <Fragment key={group.heading}>
                                <h3>{t(group.heading)}</h3>
                                <List items={group.items} />
                            </Fragment>
                        ))}

                        {section.items !== undefined && <List items={section.items} />}

                        {section.box !== undefined && (
                            <div className={section.boxTone === 'warn' ? 'warn-box' : 'info-box'}>
                                <div className={section.boxTone === 'warn' ? 'warn-box-icon' : 'info-box-icon'}>
                                    {section.boxTone === 'warn' ? <WarningIcon /> : <ShieldIcon />}
                                </div>
                                <p>{t(section.box)}</p>
                            </div>
                        )}

                        {section.outro !== undefined && <p>{t(section.outro)}</p>}

                        {section.cookiePreferences === true && <CookiePreferences />}

                        {section.contact !== undefined && <ContactCard heading={section.contact} />}
                    </section>
                </Fragment>
            ))}
        </>
    );
}

/**
 * The card at the foot of each document.
 *
 * The address and the number come from `company` and are rendered as real `mailto:` and `tel:`
 * links. The live page keeps them inside the translated string as raw HTML with an inline `style`
 * attribute — which puts the company's contact details in two translation tables, where they can
 * be corrected in one language and not the other.
 */
function ContactCard({ heading }: { heading: TranslationKey }) {
    const { t } = useTranslation();

    return (
        <div className="contact-card">
            <div className="contact-card-icon">
                <MailIcon />
            </div>
            <div className="contact-card-text">
                <h4>{t(heading)}</h4>
                <p>
                    {t('policy.contact.email')}: <a href={`mailto:${company.email}`}>{company.email}</a>
                    <br />
                    {t('policy.contact.phone')}: <a href={telHref(company.phone)}>{company.phone}</a>
                </p>
            </div>
        </div>
    );
}

function List({ items }: { items: readonly TranslationKey[] }) {
    const { t } = useTranslation();

    return (
        <ul>
            {items.map((item) => (
                <li key={item}>{t(item)}</li>
            ))}
        </ul>
    );
}
